import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { applyTheme } from "@/lib/theme";

const ContentContext = createContext(null);

export function ContentProvider({ children }) {
  const location = useLocation();
  const preview = useMemo(() => new URLSearchParams(location.search).get("preview") === "1", [location.search]);
  const [state, setState] = useState({ content: null, loading: true, error: false, preview: false });

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        if (preview) {
          const { data } = await api.get("/admin/content");
          if (alive) setState({ content: data.draft, loading: false, error: false, preview: true });
          return;
        }
        const { data } = await api.get("/content");
        if (alive) setState({ content: data.content, loading: false, error: false, preview: false });
      } catch {
        if (alive) setState((s) => ({ ...s, loading: false, error: true }));
      }
    };
    load();
    const id = setInterval(load, 30000);
    const onFocus = () => document.visibilityState === "visible" && load();
    document.addEventListener("visibilitychange", onFocus);
    return () => { alive = false; clearInterval(id); document.removeEventListener("visibilitychange", onFocus); };
  }, [preview]);

  useEffect(() => {
    if (!state.content) return;
    applyTheme(state.content.theme);
    document.title = state.content.seo?.site_title || "EDEN RP";
    const meta = document.querySelector('meta[name="description"]');
    if (meta && state.content.seo?.description) meta.setAttribute("content", state.content.seo.description);
  }, [state.content]);

  return <ContentContext.Provider value={state}>{children}</ContentContext.Provider>;
}

export const useContent = () => useContext(ContentContext);
